import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Copy, Check, ExternalLink } from "lucide-react";
import { getTeammate } from "@/lib/api";
import { chatgptUrl } from "@/lib/chatgptLink";
import type { Teammate } from "@/lib/types";
import { TeammateCard } from "@/components/TeammateCard";
import { TryItNow } from "@/components/TryItNow";
import { Orb } from "@/components/Orb";

export default function TeammatePage() {
  const { id } = useParams();
  const [teammate, setTeammate] = useState<Teammate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    getTeammate(id).then(setTeammate).catch((e) => setError(String(e)));
  }, [id]);

  async function copyPrompt() {
    if (!teammate) return;
    await navigator.clipboard.writeText(teammate.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  if (error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center text-center px-6">
        <Orb size={160} />
        <p className="mt-8 text-white/70">This associate couldn't be found.</p>
        <Link to="/" className="btn-primary mt-6">
          Build your own <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  if (!teammate) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Orb size={160} intensity="thinking" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh px-5 py-8">
      <div className="mx-auto max-w-xl">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
            Shared AI associate · PLMJ
          </div>
        </div>

        <TeammateCard teammate={teammate} className="mt-6" />

        <TryItNow teammate={teammate} />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button className="btn-ghost" onClick={copyPrompt}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy prompt"}
          </button>
          <a
            className="btn-ghost"
            href={chatgptUrl(teammate)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="h-4 w-4" />
            Open in ChatGPT
          </a>
        </div>
        <Link to="/" className="btn-primary mt-3 w-full">
          Build mine <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
